import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SingleMessages } from 'src/app/shared/components/single-message-editor/single-messages';
import { EditorConfig } from 'src/app/shared/editor-config';
import { SiteV2 } from 'src/app/shared/models';
import { Editor2Answer } from 'src/app/shared/models/misc.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { UploadService } from 'src/app/shared/services/upload.service';
import { SiteTypeEnum } from '../../enums';
import { FavoritesService } from '../../services/favorites.service';

/**
 * Wrapper for SingleMessageEditorComponent, to be able to manipulate code mirror editor as a formControl
 */
@Component({
  selector: 'app-message-editor-wrapper',
  templateUrl: './message-editor-wrapper.component.html',
})
export class MessageEditorWrapperComponent implements OnInit, OnDestroy {
  @Input() control: FormControl | AbstractControl;
  @Input() editorInitialContent: Editor2Answer[];
  @Input() isPrivate!: boolean;
  @Input() isPublish!: boolean;
  @Input() isEngagement!: boolean;

  singleMessages: SingleMessages;
  favoritesAllLang = [];
  editorConfig: EditorConfig;
  _site: SiteV2;

  isEditorValid = false;

  @Input()
  set site(site: SiteV2) {
    if (site) {
      this._site = site;

      if (this.singleMessages.getEditor2Answer().length) {
        // Only twitter allows more than one editor
        if (this._site.typeName === SiteTypeEnum.Twitter) {
          this.initEditor(this.singleMessages.getEditor2Answer());
        } else {
          this.initEditor(this.singleMessages.getEditor2Answer().slice(0, 1));
        }
      }
    }
  }

  private subscriptions = new Subscription();

  constructor(
    private alcmeonContext: AlcmeonContextService,
    private basicService: BasicService,
    private uploadService: UploadService,
    private favoritesService: FavoritesService
  ) {
    this.singleMessages = new SingleMessages(this.basicService, this.uploadService, this.alcmeonContext);
  }

  ngOnInit() {
    this.control.addValidators(this.editorsValidator());

    this.subscriptions.add(
      this.singleMessages.updateValidity$.subscribe(isValid => {
        const editorContent = this.singleMessages.getEditor2Answer();

        // there is no use case where there is no editor
        // if empty, it's during initialisation, but at the end there is at least one editor (because of this.singleMessages.addMessage();)
        if (editorContent.length) {
          this.isEditorValid = isValid;
          this.control.setValue(editorContent);
        }
      })
    );

    this.initEditor(this.editorInitialContent);
    if (this.isEngagement) {
      this.favoritesService
        .getFavorites()
        .subscribe(
          result =>
            (this.favoritesAllLang = this.favoritesService.processAndFilterFavorites(
              result.favorites,
              this._site as any,
              null,
              null,
              [],
              true
            ))
        );
    } else {
      this.favoritesAllLang = [];
    }
  }

  private initEditor(editorInitialContent: Editor2Answer[]) {
    this.singleMessages.setSiteId(this._site.id);
    this.editorConfig = new EditorConfig(this._site.typeName, {
      isPrivate: this.isPrivate,
      isPublish: this.isPublish,
      isSpellCheckerEnabledForCompany: this.alcmeonContext.isSpellCheckerEnabledForCompany(),
      isEngagement: this.isEngagement,
    });

    this.singleMessages.setEditorConfig(this.editorConfig);
    this.singleMessages.removeAllMessages();

    if (editorInitialContent) {
      this.singleMessages.initWithEditor2Answers(editorInitialContent);
    } else {
      this.singleMessages.addMessage();
    }

    this.singleMessages.lastMessage().doFocusAfterTimeout();
  }

  /**
   * A simple validator that read a boolean (which is returned by updateValidity$)
   */
  private editorsValidator(): ValidatorFn {
    return (): ValidationErrors | null => {
      if (this.isEditorValid) {
        return null;
      } else {
        return { 'editor-empty': true };
      }
    };
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
  }
}
